import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useUser } from '../contexts/UserContext'
import { asaasSubcontaService } from '../services/asaasSubconta'
import { supabase } from '../supabaseClient'
import { showToast } from '../Toast'

export default function Saque() {
  const { userId } = useUser()
  const [saldo, setSaldo] = useState({
    disponivel: 0,
    a_receber: 0,
    bloqueado: 0
  })
  const [valor, setValor] = useState('')
  const [loading, setLoading] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [historico, setHistorico] = useState([])
  const [temConta, setTemConta] = useState(false)
  const [userData, setUserData] = useState(null)
  
  // Chave PIX para saque
  const [chavePix, setChavePix] = useState('')
  const [tipoChavePix, setTipoChavePix] = useState('CPF')
  const [editandoChavePix, setEditandoChavePix] = useState(false)
  const [salvandoChavePix, setSalvandoChavePix] = useState(false)
  
  // Formulário de criação de conta
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    cpfCnpj: '',
    celular: '',
    birthDate: '',
    companyType: 'MEI',
    incomeValue: '',
    endereco: '',
    numero: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: ''
  })
  const [onboardingUrl, setOnboardingUrl] = useState(null)
  const [criandoConta, setCriandoConta] = useState(false)

  useEffect(() => {
    carregarDados()
    // Atualizar saldo a cada 30 segundos
    const interval = setInterval(carregarDados, 30000)
    return () => clearInterval(interval)
  }, [userId])

  const carregarDados = async () => {
    try {
      setCarregando(true)
      
      // Buscar dados do usuário
      const { data: user } = await supabase
        .from('usuarios')
        .select('*')
        .eq('id', userId)
        .single()
      
      setUserData(user)
      
      // Verificar se tem conta ClickPag criada (tem asaas_account_id)
      if (user?.asaas_account_id) {
        console.log('Conta ClickPag encontrada:', user.asaas_account_id)
        setTemConta(true)
        
        // Carregar chave PIX cadastrada
        if (user.chave_pix) {
          setChavePix(user.chave_pix)
          setTipoChavePix(user.tipo_chave_pix || 'CPF')
        }
        
        // Verificar se precisa de onboarding (envio de documentos)
        if (user.asaas_onboarding_url && user.asaas_account_status !== 'APPROVED') {
          console.log('Onboarding pendente:', user.asaas_onboarding_url)
          setOnboardingUrl(user.asaas_onboarding_url)
        } else {
          // Conta aprovada, consultar saldo
          const saldoResult = await asaasSubcontaService.consultarSaldo(userId)
          if (saldoResult.success) {
            setSaldo(saldoResult.saldo)
          }
          
          // Buscar histórico
          const hist = await asaasSubcontaService.getHistoricoSaques(userId)
          setHistorico(hist)
        }
      } else {
        console.log('Conta ClickPag não criada')
        setTemConta(false)
        
        // Pre-preencher formulário se tiver dados
        if (user) {
          setFormData(prev => ({
            ...prev,
            nome: user.nome_empresa || user.nome_completo || '',
            email: user.email || '',
            cpfCnpj: user.cnpj || user.cpf_cnpj || '',
            celular: user.telefone || user.celular || ''
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setCarregando(false)
    }
  }

  const handleCriarConta = async (e) => {
    e.preventDefault()
    
    // Validação de campos obrigatórios
    if (!formData.nome || !formData.email || !formData.cpfCnpj || !formData.celular) {
      showToast('Preencha todos os campos obrigatórios', 'error')
      return
    }
    
    // Validação de endereço completo
    if (!formData.endereco || !formData.numero || !formData.bairro || !formData.cep || !formData.cidade || !formData.estado) {
      showToast('Endereço completo é obrigatório', 'error')
      return
    }
    
    // Validação de CPF/CNPJ
    const cpfCnpjLimpo = formData.cpfCnpj.replace(/\D/g, '')
    if (cpfCnpjLimpo.length !== 11 && cpfCnpjLimpo.length !== 14) {
      showToast('CPF ou CNPJ inválido', 'error')
      return
    }
    
    // Validação de data de nascimento para PF
    if (cpfCnpjLimpo.length === 11 && !formData.birthDate) {
      showToast('Data de nascimento é obrigatória para CPF', 'error')
      return
    }
    
    setCriandoConta(true)
    
    console.log('Iniciando criação de conta...', formData)
    
    try {
      // Pegar sessão atual
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      console.log('Sessão:', session ? 'OK' : 'Não encontrada', 'Erro:', sessionError)
      
      if (sessionError || !session) {
        showToast('Sessão expirada. Faça login novamente.', 'error')
        setCriandoConta(false)
        return
      }
      
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      console.log('Supabase URL:', supabaseUrl)
      
      if (!supabaseUrl) {
        showToast('Configuração do servidor não encontrada', 'error')
        setCriandoConta(false)
        return
      }
      
      const url = `${supabaseUrl}/functions/v1/criar-conta-clickpag`
      console.log('Chamando URL:', url)
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify(formData)
      })
      
      console.log('Response status:', response.status)
      
      const result = await response.json()
      console.log('Result:', result)
      
      if (result.success) {
        showToast('Conta ClickPag criada com sucesso!', 'success')
        if (result.data?.onboardingUrl) {
          setOnboardingUrl(result.data.onboardingUrl)
          showToast('Envie seus documentos para ativar a conta', 'info')
        } else {
          setTemConta(true)
          carregarDados()
        }
      } else {
        console.error('Erro da API:', result.error)
        showToast(result.error || 'Erro ao criar conta', 'error')
      }
    } catch (error) {
      console.error('Erro completo:', error)
      showToast('Erro ao criar conta: ' + error.message, 'error')
    } finally {
      setCriandoConta(false)
    }
  }

  const handleSaque = async (e) => {
    e.preventDefault()
    
    const valorNumerico = parseFloat(valor.replace('R$', '').replace(/\./g, '').replace(',', '.'))
    
    if (!valorNumerico || valorNumerico <= 0) {
      showToast('Digite um valor válido', 'error')
      return
    }

    if (valorNumerico < 5) {
      showToast('Valor mínimo para saque é R$ 5,00', 'error')
      return
    }

    if (valorNumerico > saldo.disponivel) {
      showToast('Saldo insuficiente', 'error')
      return
    }

    if (!chavePix.trim()) {
      showToast('Cadastre uma chave PIX para realizar o saque', 'error')
      setEditandoChavePix(true)
      return
    }

    setLoading(true)
    try {
      const result = await asaasSubcontaService.solicitarSaque(userId, valorNumerico, chavePix, tipoChavePix)
      
      if (result.success) {
        showToast(result.message, 'success')
        setValor('')
        carregarDados()
      } else {
        showToast(result.error, 'error')
      }
    } catch (error) {
      showToast('Erro ao solicitar saque', 'error')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0)
  }

  const setValorPercentual = (percent) => {
    const novoValor = (saldo.disponivel * percent).toFixed(2).replace('.', ',')
    setValor(novoValor)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'processado': return { bg: '#dcfce7', color: '#166534' }
      case 'pendente': return { bg: '#fef9c3', color: '#854d0e' }
      case 'falhou': return { bg: '#fef2f2', color: '#dc2626' }
      default: return { bg: '#f1f5f9', color: '#64748b' }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // Salvar chave PIX para saque
  const handleSalvarChavePix = async () => {
    if (!chavePix.trim()) {
      showToast('Digite uma chave PIX válida', 'error')
      return
    }

    // Validação conforme o tipo de chave
    const chaveLimpa = chavePix.replace(/\D/g, '')
    if (tipoChavePix === 'CPF' && chaveLimpa.length !== 11) {
      showToast('CPF deve ter 11 dígitos', 'error')
      return
    }
    if (tipoChavePix === 'CNPJ' && chaveLimpa.length !== 14) {
      showToast('CNPJ deve ter 14 dígitos', 'error')
      return
    }
    if (tipoChavePix === 'PHONE' && chaveLimpa.length < 10) {
      showToast('Telefone deve ter pelo menos 10 dígitos', 'error')
      return
    }
    if (tipoChavePix === 'EMAIL' && !validarEmail(chavePix)) {
      showToast('Digite um email válido', 'error')
      return
    }

    setSalvandoChavePix(true)
    try {
      const { error } = await supabase
        .from('usuarios')
        .update({
          chave_pix: chavePix.trim(),
          tipo_chave_pix: tipoChavePix,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)

      if (error) throw error

      showToast('Chave PIX salva com sucesso!', 'success')
      setEditandoChavePix(false)
    } catch (error) {
      console.error('Erro ao salvar chave PIX:', error)
      showToast('Erro ao salvar chave PIX', 'error')
    } finally {
      setSalvandoChavePix(false)
    }
  }

  // Detectar tipo de chave PIX automaticamente
  const detectarTipoChavePix = (valor) => {
    const limpo = valor.replace(/\D/g, '')
    if (valor.includes('@')) return 'EMAIL'
    if (limpo.length === 11) return 'CPF'
    if (limpo.length === 14) return 'CNPJ'
    if (valor.length === 36 && valor.includes('-')) return 'EVP'
    if (limpo.length >= 10 && limpo.length <= 11) return 'PHONE'
    return 'CPF'
  }

  // Funções de máscara para chave PIX
  const formatarCPF = (valor) => {
    return valor.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').substring(0, 14)
  }

  const formatarCNPJ = (valor) => {
    return valor.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2').substring(0, 18)
  }

  const formatarTelefone = (valor) => {
    return valor.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{4})$/, '$1-$2').substring(0, 15)
  }

  const formatarChavePix = (valor, tipo) => {
    switch (tipo) {
      case 'CPF': return formatarCPF(valor)
      case 'CNPJ': return formatarCNPJ(valor)
      case 'PHONE': return formatarTelefone(valor)
      case 'EMAIL': return valor // email não tem máscara
      case 'EVP': return valor // chave aleatória não tem máscara
      default: return valor
    }
  }

  // Validar email
  const validarEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  if (carregando) {
    return (
      <div style={{ padding: '32px', maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
        <Icon icon="mdi:loading" width="32" className="spin" />
        <p>Carregando...</p>
      </div>
    )
  }

  // Tela de onboarding (envio de documentos)
  if (onboardingUrl) {
    return (
      <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '24px', padding: '40px', color: 'white', textAlign: 'center'
        }}>
          <Icon icon="mdi:file-document-check" width="64" style={{ marginBottom: '20px' }} />
          <h1 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '12px' }}>
            Envio de Documentos Necessário
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9, maxWidth: '500px', margin: '0 auto 24px' }}>
            Sua Conta ClickPag foi criada! Para começar a receber pagamentos, 
            precisamos validar seus documentos.
          </p>
          
          <a
            href={onboardingUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'white', color: '#d97706', padding: '16px 32px',
              borderRadius: '12px', fontWeight: '700', textDecoration: 'none',
              fontSize: '16px', boxShadow: '0 4px 14px rgba(0,0,0,0.2)'
            }}
          >
            <Icon icon="mdi:external-link" width="20" />
            Enviar Documentos
          </a>
          
          <p style={{ fontSize: '13px', opacity: 0.8, marginTop: '16px' }}>
            Você será redirecionado para uma página segura (cadastro.io)
          </p>
        </div>

        <div style={{ marginTop: '24px', background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon icon="mdi:information" color="#f59e0b" />
            O que você precisa enviar?
          </h3>
          <ul style={{ paddingLeft: '24px', color: '#475569', lineHeight: '1.8' }}>
            <li>Documento de identificação (RG, CNH ou CPF)</li>
            <li>Selfie para reconhecimento facial</li>
            <li>Comprovante de endereço (para empresas)</li>
          </ul>
          <p style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '8px', fontSize: '14px', color: '#92400e' }}>
            <Icon icon="mdi:clock" style={{ marginRight: '8px' }} />
            A análise é feita em até 48 horas úteis. Você receberá uma notificação quando sua conta for aprovada.
          </p>
        </div>
      </div>
    )
  }

  // Tela de criação de conta ClickPag
  if (!temConta) {
    return (
      <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '20px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Icon icon="mdi:bank-plus" width="40" style={{ color: 'white' }} />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>
            Criar Conta ClickPag
          </h1>
          <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto' }}>
            Você precisa criar uma conta ClickPag para receber pagamentos dos seus clientes e realizar saques.
          </p>
        </div>

        <div style={{
          background: 'white', borderRadius: '24px', padding: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9'
        }}>
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px',
            padding: '16px', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start'
          }}>
            <Icon icon="mdi:information" width="20" style={{ color: '#2563eb', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#1e40af' }}>
              <strong>Por que preciso disso?</strong><br />
              A Conta ClickPag é sua conta de recebimento. É através dela que seus clientes pagarão suas mensalidades via PIX ou Boleto, e você poderá sacar seu saldo.
            </div>
          </div>

          <form onSubmit={handleCriarConta} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Nome da Empresa *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Ex: Minha Empresa LTDA"
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@empresa.com"
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  CPF ou CNPJ *
                </label>
                <input
                  type="text"
                  name="cpfCnpj"
                  value={formData.cpfCnpj}
                  onChange={(e) => {
                    const valor = e.target.value
                    const limpo = valor.replace(/\D/g, '')
                    let formatado = valor
                    if (limpo.length <= 11) {
                      formatado = formatarCPF(valor)
                    } else {
                      formatado = formatarCNPJ(valor)
                    }
                    setFormData(prev => ({ ...prev, cpfCnpj: formatado }))
                  }}
                  placeholder="00.000.000/0000-00"
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Celular *
                </label>
                <input
                  type="text"
                  name="celular"
                  value={formData.celular}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, celular: formatarTelefone(e.target.value) }))
                  }}
                  placeholder="(00) 00000-0000"
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Campos condicionais baseado no tipo de documento */}
            {formData.cpfCnpj && formData.cpfCnpj.replace(/\D/g, '').length === 11 && (
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Data de Nascimento *
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
            )}
            
            {formData.cpfCnpj && formData.cpfCnpj.replace(/\D/g, '').length === 14 && (
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Tipo de Empresa *
                </label>
                <select
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="MEI">MEI (Microempreendedor Individual)</option>
                  <option value="LIMITED">LTDA (Sociedade Limitada)</option>
                  <option value="INDIVIDUAL">Empresário Individual</option>
                  <option value="ASSOCIATION">Associação</option>
                </select>
              </div>
            )}
            
            {/* Renda/Faturamento */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Renda/Faturamento Mensal (R$) *
              </label>
              <input
                type="number"
                name="incomeValue"
                value={formData.incomeValue}
                onChange={handleInputChange}
                placeholder="5000"
                min="0"
                step="0.01"
                required
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                }}
              />
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', marginTop: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon icon="mdi:map-marker" color="#7c3aed" />
                Endereço
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleInputChange}
                    placeholder="Rua, Avenida, etc."
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Número
                  </label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    placeholder="123"
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Bairro
                  </label>
                  <input
                    type="text"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange}
                    placeholder="Centro"
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '16px', marginTop: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    CEP
                  </label>
                  <input
                    type="text"
                    name="cep"
                    value={formData.cep}
                    onChange={(e) => {
                      const cepFormatado = e.target.value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').substring(0, 9)
                      setFormData(prev => ({ ...prev, cep: cepFormatado }))
                    }}
                    placeholder="00000-000"
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange}
                    placeholder="São Paulo"
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Estado
                  </label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none',
                      background: 'white'
                    }}
                  >
                    <option value="">UF</option>
                    {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={criandoConta}
              style={{
                marginTop: '16px',
                padding: '16px 32px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                cursor: criandoConta ? 'not-allowed' : 'pointer',
                opacity: criandoConta ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {criandoConta ? (
                <><Icon icon="mdi:loading" width="20" className="spin" /> Criando conta...</>
              ) : (
                <><Icon icon="mdi:check-circle" width="20" /> Criar Conta ClickPag</>
              )}
            </button>

            <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '8px' }}>
              Ao criar a conta, você concorda com os termos de uso da ClickPag e Asaas.
            </p>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>
          Saque para Conta Bancária
        </h1>
        <p style={{ color: '#64748b', fontSize: '16px' }}>
          Transfira seu saldo da sua Conta ClickPag via PIX instantâneo
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
        
        {/* Coluna da Esquerda: Saldo */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Card Principal de Saldo */}
          <div style={{ 
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', 
            padding: '32px', 
            borderRadius: '24px', 
            color: 'white',
            boxShadow: '0 20px 25px -5px rgba(5, 150, 105, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', position: 'relative' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Saldo Disponível para Saque
              </div>
              <Icon icon="mdi:wallet-bifold-outline" width="24" style={{ opacity: 0.8 }} />
            </div>
            
            <div style={{ fontSize: '42px', fontWeight: '800', marginBottom: '24px', position: 'relative', letterSpacing: '-1px' }}>
              {formatCurrency(saldo.disponivel)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', position: 'relative' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '12px 16px', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                  <Icon icon="mdi:clock-outline" /> A liberar
                </div>
                <div style={{ fontWeight: '700', fontSize: '16px' }}>{formatCurrency(saldo.a_receber)}</div>
                <div style={{ fontSize: '10px', opacity: 0.8 }}>PIX: instantâneo | Boleto: 3 dias</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '12px 16px', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                  <Icon icon="mdi:lock-outline" /> Bloqueado
                </div>
                <div style={{ fontWeight: '700', fontSize: '16px' }}>{formatCurrency(saldo.bloqueado)}</div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div style={{ backgroundColor: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#334155', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon="mdi:information-outline" color="#059669" />
              Informações Importantes
            </h3>
            <ul style={{ paddingLeft: '20px', margin: 0, color: '#64748b', fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <li>Saque via PIX é processado <strong>instantaneamente</strong></li>
              <li>Valores de boletos ficam disponíveis após <strong>3 dias</strong></li>
              <li>Valor mínimo para saque: <strong>R$ 5,00</strong></li>
              <li>O valor será transferido para a chave PIX cadastrada</li>
            </ul>
          </div>
        </div>

        {/* Coluna da Direita: Formulário */}
        <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon icon="mdi:bank-transfer-out" width="24" color="#059669" />
            </div>
            Solicitar Saque
          </h2>

          <form onSubmit={handleSaque} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Chave PIX para Saque */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon icon="mdi:qrcode" width="18" color="#0891B2" />
                  Chave PIX para Recebimento
                </label>
                {!editandoChavePix && chavePix && (
                  <button
                    type="button"
                    onClick={() => setEditandoChavePix(true)}
                    style={{
                      fontSize: '12px',
                      color: '#0891B2',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Alterar
                  </button>
                )}
              </div>

              {editandoChavePix ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <select
                    value={tipoChavePix}
                    onChange={(e) => setTipoChavePix(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="EMAIL">E-mail</option>
                    <option value="PHONE">Celular</option>
                    <option value="EVP">Chave Aleatória (EVP)</option>
                  </select>
                  <input
                    type="text"
                    value={chavePix}
                    onChange={(e) => {
                      const valorFormatado = formatarChavePix(e.target.value, tipoChavePix)
                      setChavePix(valorFormatado)
                      // Auto-detectar tipo se não estiver editando manualmente
                      const detectado = detectarTipoChavePix(e.target.value)
                      if (detectado !== tipoChavePix && detectado !== 'CPF') {
                        setTipoChavePix(detectado)
                      }
                    }}
                    placeholder={tipoChavePix === 'EMAIL' ? 'email@exemplo.com' : tipoChavePix === 'PHONE' ? '(11) 98765-4321' : tipoChavePix === 'EVP' ? '00000000-0000-0000-0000-000000000000' : tipoChavePix === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={handleSalvarChavePix}
                      disabled={salvandoChavePix}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#0891B2',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        opacity: salvandoChavePix ? 0.7 : 1
                      }}
                    >
                      {salvandoChavePix ? 'Salvando...' : 'Salvar Chave'}
                    </button>
                    {chavePix && (
                      <button
                        type="button"
                        onClick={() => setEditandoChavePix(false)}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          background: 'white',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    padding: '10px 12px',
                    background: chavePix ? '#ecfdf5' : '#fef3c7',
                    borderRadius: '8px',
                    border: `1px solid ${chavePix ? '#a7f3d0' : '#fcd34d'}`,
                    flex: 1
                  }}>
                    {chavePix ? (
                      <div>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{tipoChavePix}: </span>
                        <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>{chavePix}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#92400e' }}>Nenhuma chave PIX cadastrada</span>
                    )}
                  </div>
                  {!chavePix && (
                    <button
                      type="button"
                      onClick={() => setEditandoChavePix(true)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#f59e0b',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cadastrar
                    </button>
                  )}
                </div>
              )}
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                Esta chave PIX será usada para receber seus saques. Pode ser diferente do CNPJ da empresa.
              </p>
            </div>

            {/* Input de Valor */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                Valor do Saque
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '600' }}>
                  R$
                </span>
                <input
                  type="text"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  placeholder="0,00"
                  style={{ 
                    width: '100%', 
                    padding: '16px 16px 16px 44px', 
                    borderRadius: '12px', 
                    border: '1px solid #e2e8f0', 
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1e293b',
                    outline: 'none'
                  }}
                  required
                />
              </div>
              
              {/* Botões de percentual */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                {[0.25, 0.5, 0.75, 1].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setValorPercentual(pct)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    {pct === 1 ? '100%' : `${(pct * 100).toFixed(0)}%`}
                  </button>
                ))}
              </div>
            </div>

            {/* Resumo */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: '#64748b' }}>Valor do saque:</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(parseFloat(valor.replace(/[R$\.,]/g, '').replace(/(\d{2})$/, '.$1')) || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#64748b' }}>Taxa:</span>
                <span style={{ fontWeight: '600', color: '#16a34a' }}>Grátis</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !valor || parseFloat(valor.replace(/[R$\.,]/g, '').replace(/(\d{2})$/, '.$1')) <= 0}
              style={{
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <><Icon icon="mdi:loading" width="20" className="spin" /> Processando...</>
              ) : (
                <><Icon icon="mdi:send" width="20" /> Solicitar Saque</>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Histórico */}
      <div style={{ marginTop: '40px' }}>
        <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '20px' }}>
          Histórico de Saques
        </h3>
        
        {historico.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px' }}>
            <Icon icon="mdi:cash-clock" width="48" style={{ marginBottom: '12px' }} />
            <p>Nenhum saque realizado ainda</p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Data</th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Valor</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((item) => {
                  const statusStyle = getStatusColor(item.status)
                  return (
                    <tr key={item.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#1e293b' }}>
                        {formatDate(item.created_at)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                        {formatCurrency(item.valor)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          textTransform: 'capitalize'
                        }}>
                          {item.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                        {item.pix_chave ? 'PIX' : 'Transferência'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
